'use client';

import clsx from 'clsx';
import { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useBenzingaEdge } from '@benzinga/edge';
import { Pagination } from '@benzinga/core-ui';
import { AiOutlineUnlock } from 'react-icons/ai';
import { MemberCard } from '../../../../src/components/GovTrades';
import {
  filterMembersList,
  MEMBERS_SORT_OPTIONS,
  sortMembersList,
  type MembersChamberFilter,
  type MembersListItem,
  type MembersPartyFilter,
  type MembersSortKey,
  type MembersSortOrder,
} from './utils';
import { FilterField } from './FilterField';

const BzEdgeCTA = dynamic(() => import('@benzinga/ads').then(module => module.BzEdgeCTA), { ssr: true });

const PAGE_SIZE = 10;

const CHAMBER_OPTIONS: { value: MembersChamberFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'house', label: 'House' },
  { value: 'senate', label: 'Senate' },
];

const PARTY_OPTIONS: { value: MembersPartyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'D', label: 'Democrat' },
  { value: 'R', label: 'Republican' },
  { value: 'I', label: 'Independent' },
];

export const MembersGrid = ({ members }: { members: MembersListItem[] }) => {
  const { hasEdgeSubscription: hasPermission, isEdgeSubscriptionLoading } = useBenzingaEdge();
  const [page, setPage] = useState(1);
  const [chamber, setChamber] = useState<MembersChamberFilter>('all');
  const [party, setParty] = useState<MembersPartyFilter>('all');
  const [sortKey, setSortKey] = useState<MembersSortKey>('date_of_last_recorded_trade');
  const [sortOrder, setSortOrder] = useState<MembersSortOrder>('desc');

  const filteredSorted = useMemo(() => {
    const filtered = filterMembersList(members ?? [], chamber, party);
    return sortMembersList(filtered, sortKey, sortOrder);
  }, [members, chamber, party, sortKey, sortOrder]);

  const pageMembers = useMemo(() => {
    if (!hasPermission) return filteredSorted.slice(0, PAGE_SIZE);
    const start = (page - 1) * PAGE_SIZE;
    return filteredSorted.slice(start, start + PAGE_SIZE);
  }, [filteredSorted, hasPermission, page]);

  const locked = !isEdgeSubscriptionLoading && !hasPermission;
  const controlsDisabled = isEdgeSubscriptionLoading || !hasPermission;

  const resetPage = () => setPage(1);

  const handleChamber = (value: MembersChamberFilter) => {
    setChamber(value);
    resetPage();
  };

  const handleParty = (value: MembersPartyFilter) => {
    setParty(value);
    resetPage();
  };

  const handleSort = (value: string) => {
    const [key, order] = value.split('+') as [MembersSortKey, MembersSortOrder];
    setSortKey(key);
    setSortOrder(order);
    resetPage();
  };

  const handlePageChange = ({ nextPage }: { nextPage: number }) => {
    setPage(nextPage);
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 flex items-center gap-2 text-2xl font-extrabold text-bzgray-900">
          Congress Members{' '}
          <span className="text-sm font-semibold text-bzgray-600">
            ({filteredSorted.length.toLocaleString()} active traders)
          </span>
          <span className="edge-mini">EDGE</span>
        </h2>
        <div
          className={clsx(
            'group relative ml-auto flex flex-wrap items-center gap-2 max-sm:ml-0 max-sm:w-full',
            controlsDisabled && '[&_label]:opacity-55 [&_select]:cursor-not-allowed',
          )}
        >
          <FilterField label="Chamber">
            <select
              aria-label="Filter by chamber"
              disabled={controlsDisabled}
              onChange={e => handleChamber(e.target.value as MembersChamberFilter)}
              value={chamber}
            >
              {CHAMBER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Party">
            <select
              aria-label="Filter by party"
              disabled={controlsDisabled}
              onChange={e => handleParty(e.target.value as MembersPartyFilter)}
              value={party}
            >
              {PARTY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Sort">
            <select
              aria-label="Sort members"
              disabled={controlsDisabled}
              onChange={e => handleSort(e.target.value)}
              value={`${sortKey}+${sortOrder}`}
            >
              {MEMBERS_SORT_OPTIONS.map(opt => (
                <option key={`${opt.key}+${opt.order}`} value={`${opt.key}+${opt.order}`}>
                  {opt.name}
                </option>
              ))}
            </select>
          </FilterField>

          {locked ? (
            <a
              className="absolute inset-0 z-[2] hidden items-center justify-center gap-1.5 rounded-lg bg-white/55 text-[13px] font-bold leading-tight text-[#3f6df6] no-underline backdrop-blur-[2px] group-hover:flex"
              href="https://www.benzinga.com/premium/ideas/benzinga-edge-checkout/?t=be8be9we4gewe1be11&utm_source=government-trades"
              target="_blank"
              rel="noreferrer"
            >
              <AiOutlineUnlock size={16} /> Unlock with Benzinga Edge
            </a>
          ) : null}
        </div>
      </div>

      {filteredSorted.length === 0 ? (
        <p className="py-6 text-sm font-semibold text-[#5b6b82]">No members match these filters.</p>
      ) : (
        <>
          <div className="relative grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pageMembers.map(member => (
              <MemberCard key={member.filer_info?.member_id ?? member.id} {...member} />
            ))}
          </div>

          {hasPermission ? (
            <div className="mt-3.5 py-2 max-md:mt-5 max-md:px-1 max-md:pb-6 max-md:pt-4">
              <Pagination
                buttonClassName="pagination-button"
                defaultPage={page}
                onPageChanged={handlePageChange}
                pageSize={PAGE_SIZE}
                totalItems={filteredSorted.length}
              />
            </div>
          ) : null}

          {locked && filteredSorted.length > PAGE_SIZE ? (
            <div className="mt-4 flex items-center justify-center">
              <BzEdgeCTA type="gov-trades" />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
